// Career Fit Predictor - Multi-dimensional career environment matching
import { FEATURE_FLAGS } from '../config/featureFlags.js';

// Career environment profiles
const CAREER_ENVIRONMENTS = {
  'research_development': {
    name: 'Research & Development',
    traits: {
      openness: 0.8,
      conscientiousness: 0.7,
      extraversion: 0.3,
      agreeableness: 0.5,
      neuroticism: 0.2,
      emotional_intelligence: 0.6,
      cognitive_ability: 0.9,
      mindfulness: 0.7,
      innovation: 0.9,
      analytical: 0.8
    },
    description: 'Innovation-focused roles requiring deep analysis and creative problem-solving'
  },
  'leadership': {
    name: 'Leadership & Management',
    traits: {
      openness: 0.6,
      conscientiousness: 0.8,
      extraversion: 0.8,
      agreeableness: 0.7,
      neuroticism: 0.2,
      emotional_intelligence: 0.9,
      cognitive_ability: 0.7,
      mindfulness: 0.6,
      communication: 0.9,
      decision_making: 0.8
    },
    description: 'People-focused roles requiring strategic thinking and team coordination'
  },
  'operations': {
    name: 'Operations & Process Management',
    traits: {
      openness: 0.4,
      conscientiousness: 0.9,
      extraversion: 0.5,
      agreeableness: 0.6,
      neuroticism: 0.2,
      emotional_intelligence: 0.6,
      cognitive_ability: 0.6,
      mindfulness: 0.5,
      systematic: 0.9,
      reliability: 0.8
    },
    description: 'Structure-focused roles emphasizing efficiency and systematic execution'
  },
  'consulting': {
    name: 'Consulting & Advisory',
    traits: {
      openness: 0.7,
      conscientiousness: 0.7,
      extraversion: 0.7,
      agreeableness: 0.6,
      neuroticism: 0.3,
      emotional_intelligence: 0.8,
      cognitive_ability: 0.8,
      mindfulness: 0.6,
      adaptability: 0.8,
      communication: 0.8
    },
    description: 'Client-focused roles requiring adaptability and strategic problem-solving'
  },
  'creative': {
    name: 'Creative & Design',
    traits: {
      openness: 0.9,
      conscientiousness: 0.5,
      extraversion: 0.4,
      agreeableness: 0.5,
      neuroticism: 0.4,
      emotional_intelligence: 0.7,
      cognitive_ability: 0.6,
      mindfulness: 0.8,
      creativity: 0.9,
      aesthetic: 0.8
    },
    description: 'Expression-focused roles emphasizing innovation and aesthetic sensibility'
  },
  'technical': {
    name: 'Technical & Engineering',
    traits: {
      openness: 0.6,
      conscientiousness: 0.8,
      extraversion: 0.3,
      agreeableness: 0.4,
      neuroticism: 0.2,
      emotional_intelligence: 0.5,
      cognitive_ability: 0.9,
      mindfulness: 0.6,
      logical: 0.9,
      systematic: 0.8
    },
    description: 'Logic-focused roles requiring precision and technical expertise'
  },
  'sales_business': {
    name: 'Sales & Business Development',
    traits: {
      openness: 0.6,
      conscientiousness: 0.6,
      extraversion: 0.9,
      agreeableness: 0.7,
      neuroticism: 0.2,
      emotional_intelligence: 0.8,
      cognitive_ability: 0.6,
      mindfulness: 0.5,
      persuasion: 0.9,
      resilience: 0.8
    },
    description: 'Relationship-focused roles emphasizing influence and business growth'
  }
};

// Calculate career fit scores
export const calculateCareerFit = (profileScores) => {
  if (!FEATURE_FLAGS.ADVANCED_SCORING) {
    return { matches: [], confidence: 0 };
  }

  const userProfile = normalizeProfile(profileScores);
  const careerMatches = [];

  // Calculate fit for each career environment
  Object.entries(CAREER_ENVIRONMENTS).forEach(([key, career]) => {
    const fitScore = calculateFitScore(userProfile, career.traits);
    const confidence = calculateConfidence(userProfile, career.traits);
    
    careerMatches.push({
      id: key,
      name: career.name,
      description: career.description,
      fitScore: Math.round(fitScore * 100),
      confidence: Math.round(confidence * 100),
      strengths: identifyStrengths(userProfile, career.traits),
      gaps: identifyGaps(userProfile, career.traits)
    });
  });

  // Sort by fit score and return top 5
  const topMatches = careerMatches
    .sort((a, b) => b.fitScore - a.fitScore)
    .slice(0, 5);

  return {
    matches: topMatches,
    confidence: calculateOverallConfidence(topMatches),
    recommendations: generateRecommendations(topMatches, userProfile)
  };
};

// Normalize profile scores to 0-1 scale
const normalizeProfile = (scores) => {
  const normalized = {};
  
  // Big Five (already 0-100, normalize to 0-1)
  if (scores.bigFive) {
    normalized.openness = scores.bigFive.openness / 100;
    normalized.conscientiousness = scores.bigFive.conscientiousness / 100;
    normalized.extraversion = scores.bigFive.extraversion / 100;
    normalized.agreeableness = scores.bigFive.agreeableness / 100;
    normalized.neuroticism = scores.bigFive.neuroticism / 100;
  }

  // Other dimensions
  normalized.emotional_intelligence = (scores.emotionalIntelligence || 50) / 100;
  normalized.cognitive_ability = (scores.cognitiveAbility || 50) / 100;
  normalized.mindfulness = (scores.mindfulness || 50) / 100;
  
  // Derived traits
  normalized.creativity = Math.max(normalized.openness || 0, 0);
  normalized.leadership = Math.max((normalized.extraversion || 0) * 0.7 + (normalized.emotional_intelligence || 0) * 0.3, 0);
  normalized.analytical = Math.max((normalized.cognitive_ability || 0) * 0.8 + (normalized.conscientiousness || 0) * 0.2, 0);
  
  return normalized;
};

// Calculate fit score using weighted euclidean distance
const calculateFitScore = (userProfile, careerTraits) => {
  let totalDistance = 0;
  let weightSum = 0;

  Object.entries(careerTraits).forEach(([trait, idealValue]) => {
    const userValue = userProfile[trait] || 0.5; // Default to neutral
    const weight = getTraitWeight(trait);
    const distance = Math.abs(userValue - idealValue);
    
    totalDistance += distance * weight;
    weightSum += weight;
  });

  const avgDistance = totalDistance / weightSum;
  return Math.max(0, 1 - avgDistance); // Convert distance to similarity
};

// Get trait importance weights
const getTraitWeight = (trait) => {
  const weights = {
    // Core personality traits
    openness: 1.0,
    conscientiousness: 1.0,
    extraversion: 1.0,
    agreeableness: 0.8,
    neuroticism: 0.8,
    // Abilities
    emotional_intelligence: 1.2,
    cognitive_ability: 1.2,
    mindfulness: 0.9,
    // Derived traits
    creativity: 0.7,
    leadership: 0.7,
    analytical: 0.7
  };
  
  return weights[trait] || 0.5;
};

// Calculate confidence in fit assessment
const calculateConfidence = (userProfile, careerTraits) => {
  let strongMatches = 0;
  let totalTraits = 0;

  Object.entries(careerTraits).forEach(([trait, idealValue]) => {
    const userValue = userProfile[trait] || 0.5;
    const difference = Math.abs(userValue - idealValue);
    
    if (difference < 0.2) strongMatches++; // Strong match threshold
    totalTraits++;
  });

  return strongMatches / totalTraits;
};

// Identify user strengths for career
const identifyStrengths = (userProfile, careerTraits) => {
  const strengths = [];
  
  Object.entries(careerTraits).forEach(([trait, idealValue]) => {
    const userValue = userProfile[trait] || 0.5;
    
    if (userValue >= idealValue - 0.1 && idealValue > 0.6) {
      strengths.push(getTraitLabel(trait));
    }
  });

  return strengths.slice(0, 3); // Top 3 strengths
};

// Identify development gaps
const identifyGaps = (userProfile, careerTraits) => {
  const gaps = [];
  
  Object.entries(careerTraits).forEach(([trait, idealValue]) => {
    const userValue = userProfile[trait] || 0.5;
    const gap = idealValue - userValue;
    
    if (gap > 0.2 && idealValue > 0.6) {
      gaps.push({
        trait: getTraitLabel(trait),
        gap: Math.round(gap * 100)
      });
    }
  });

  return gaps.slice(0, 2); // Top 2 gaps
};

// Get human-readable trait labels
const getTraitLabel = (trait) => {
  const labels = {
    openness: 'Openness to Experience',
    conscientiousness: 'Organization & Discipline',
    extraversion: 'Social Energy',
    agreeableness: 'Collaboration',
    neuroticism: 'Emotional Stability',
    emotional_intelligence: 'Emotional Intelligence',
    cognitive_ability: 'Analytical Thinking',
    mindfulness: 'Self-Awareness',
    creativity: 'Creative Thinking',
    leadership: 'Leadership Potential',
    analytical: 'Analytical Skills'
  };
  
  return labels[trait] || trait;
};

// Calculate overall confidence
const calculateOverallConfidence = (matches) => {
  if (matches.length === 0) return 0;
  
  const avgConfidence = matches.reduce((sum, match) => sum + match.confidence, 0) / matches.length;
  const topScore = matches[0]?.fitScore || 0;
  const scoreSpread = matches.length > 1 ? matches[0].fitScore - matches[1].fitScore : 0;
  
  // Higher confidence if top match is clear winner
  return Math.round(avgConfidence * (1 + scoreSpread / 100));
};

// Generate career recommendations
const generateRecommendations = (matches, userProfile) => {
  const recommendations = [];
  
  if (matches.length > 0) {
    const topMatch = matches[0];
    
    recommendations.push(`Your strongest career fit is ${topMatch.name} with ${topMatch.fitScore}% compatibility.`);
    
    if (topMatch.strengths.length > 0) {
      recommendations.push(`Your key strengths for this role include ${topMatch.strengths.join(', ')}.`);
    }
    
    if (topMatch.gaps.length > 0) {
      const primaryGap = topMatch.gaps[0];
      recommendations.push(`Consider developing ${primaryGap.trait} to increase your fit by ${primaryGap.gap}%.`);
    }
    
    if (matches.length > 1) {
      const secondMatch = matches[1];
      recommendations.push(`${secondMatch.name} is also a strong fit at ${secondMatch.fitScore}% compatibility.`);
    }
  }
  
  return recommendations;
};

export default { calculateCareerFit };